import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Menu,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  FilterList as FilterListIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  PictureAsPdf,
  InsertDriveFile,
  KeyboardDoubleArrowUp as CriticalIcon,
  Block as BlockerIcon,
  KeyboardArrowUp as HighIcon,
  HorizontalRule as MediumIcon,
  KeyboardArrowDown as LowIcon,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface Task {
  id: string;
  task_details: {
    task_name: string;
    status: string;
    assignee: {
      id: string;
      name: string;
      email_id: string;
      avatar_url: string | null;
      username: string;
    };
    created_by: { 
    avatar_url:string;
    name: string
    };
    priority: string;
    description: string;
    due_date: string;
    created_at: string;
    updated_at: string;
    source: string;
    tags: string[];
  };
  task_history: Array<{
    action: string;
    details: string;
    timestamp: string;
    user: {
      id: string;
      name: string;
      email_id: string;
      avatar_url: string | null;
      role: string;
    };
  }>;
  context: {
    workspace: {
      id: string;
      name: string;
      description: string;
    };
    channel?: {
      id: string;
      name: string;
      description: string;
      type: string;
      is_private: boolean;
    };
    thread?: {
      id: string;
      title?: string;
      description?: string;
    };
  };
}

interface TasksResponse {
  tasks: Task[];
  total: number;
  page: number;
  size: number;
}

const TasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { markSessionExpired } = useAuth();
  const { isDarkMode } = useTheme();
  const [allTasks, setAllTasks] = useState<Task[]>([]);

  // Navigate to thread and open task panel; uses channelId/threadId from API context
  const handleTaskRowClick = (task: Task) => {
    const channelId = task.context?.channel?.id;
    const threadId = task.context?.thread?.id;
    if (channelId && threadId) {
      navigate(`/threads/${channelId}/${threadId}?openTask=true`);
    }
  };
  
  // Helper function to transform "Close" to "Closed" in tasks data
  const transformTasksData = (tasks: Task[]) => {
    return tasks.map(task => ({
      ...task,
      task_details: {
        ...task.task_details,
        status: task.task_details.status === 'Close' ? 'Closed' : task.task_details.status
      }
    }));
  };

  // Helper function to transform "Closed" to "Close" for API calls
  const transformStatusForAPI = (status: string) => {
    return status === 'Closed' ? 'Close' : status;
  };

  // Helper function to get priority icon and color
  const getPriorityDisplay = (priority: string) => {
    const priorityLower = priority?.toLowerCase() || 'medium';
    
    switch (priorityLower) {
      case 'critical':
        return {
          icon: <CriticalIcon fontSize="small" />,
          color: '#ff6b35' // Orange color
        };
      case 'blocker':
        return {
          icon: <BlockerIcon fontSize="small" />,
          color: '#d32f2f' // Red color
        };
      case 'high':
        return {
          icon: <HighIcon fontSize="small" />,
          color: '#ff9800' // Orange color
        };
      case 'medium':
        return {
          icon: (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              <Box sx={{ width: 12, height: 2, backgroundColor: '#2196f3', borderRadius: 0.5 }} />
              <Box sx={{ width: 12, height: 2, backgroundColor: '#2196f3', borderRadius: 0.5 }} />
            </Box>
          ),
          color: '#2196f3' // Blue color
        };
      case 'low':
        return {
          icon: <LowIcon fontSize="small" />,
          color: '#9e9e9e' // Grey color
        };
      default:
        return {
          icon: <MediumIcon fontSize="small" />,
          color: '#2196f3'
        };
    }
  };
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    size: 20,
  });
  const [filters, setFilters] = useState({
    status: [] as string[],
    priority: '',
    assigned_to: '',
    workspace_id: '',
    channel_id: '',
    thread_id: '',
    source: '',
    tags: '',
    timeFilter: 'All',
    created_by: '',
  });
  // Searchable users dropdown (like ApprovalsPage)
  const [selectedAssignee, setSelectedAssignee] = useState<{ name: string; email: string; id: string } | null>(null);
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] = useState<null | HTMLElement>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [timeFilterMenuAnchor, setTimeFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [priorityFilterAnchor, setPriorityFilterAnchor] = useState<null | HTMLElement>(null);
  const [assigneeFilterAnchor, setAssigneeFilterAnchor] = useState<null | HTMLElement>(null);
  // Disable filter menus after a selection is applied; re-enable on clear
  const [statusFilterDisabled, setStatusFilterDisabled] = useState(false);
  const [priorityFilterDisabled, setPriorityFilterDisabled] = useState(false);
  const [assigneeFilterDisabled, setAssigneeFilterDisabled] = useState(false);
  const statusFilterBtnRef = useRef<HTMLButtonElement | null>(null);
  const priorityFilterBtnRef = useRef<HTMLButtonElement | null>(null);
  const assigneeFilterBtnRef = useRef<HTMLButtonElement | null>(null);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [statusDropdownAnchor, setStatusDropdownAnchor] = useState<null | HTMLElement>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [rowComments, setRowComments] = useState<{ [key: string]: string }>({});
  const [selectedStatuses, setSelectedStatuses] = useState<{ [key: string]: string }>({});
  const [rowAttachments, setRowAttachments] = useState<{ [key: string]: File[] }>({});
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Return one of four consistent colors for a given key (e.g., user id or name)
  const getAvatarColor = (key: string | undefined | null) => {
    const palette = ['#1976d2', '#2e7d32', '#ed6c02', '#0288d1'];
    if (!key) return palette[0];
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash << 5) - hash + key.charCodeAt(i);
      hash |= 0;
    }
    const index = Math.abs(hash) % palette.length;
    return palette[index];
  };
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'asc' | 'desc' | null;
  }>({ field: '', direction: null });
  const [createdByFilterAnchor, setCreatedByFilterAnchor] = useState<null | HTMLElement>(null);
  const [spaceFilterAnchor, setSpaceFilterAnchor] = useState<null | HTMLElement>(null);
  const [users, setUsers] = useState<Array<{
    id: string;
    name: string;
    email_id: string;
    company_id: string;
    role: string;
    is_active: boolean;
    last_login: string | null;
    created_datetime: string;
    updated_datetime: string;
  }>>([]);

  // Filter users based on search query (same as ApprovalsPage)
  const filteredUsers = users.filter(user => {
    const searchLower = userSearchQuery.toLowerCase();
    const nameMatch = user.name?.toLowerCase().includes(searchLower);
    const emailMatch = user.email_id?.toLowerCase().includes(searchLower);
    return nameMatch || emailMatch;
  });

  // Fetch users when dropdown is opened (same endpoint as ApprovalsPage)
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      const parsedStoredUserData = storedUserData ? JSON.parse(storedUserData) : null;
      if (!parsedStoredUserData?.company_id) return;

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.LIST_COMPANY_USERS(parsedStoredUserData.company_id)}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setUsers(data.users || data.items || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Pagination handlers (MUI TablePagination uses 0-based page index)
  const handleChangePage = (_event: unknown, newPageZeroBased: number) => {
    const maxPage = Math.max(1, Math.ceil(pagination.total / pagination.size));
    const clampedZeroBased = Math.min(Math.max(0, newPageZeroBased), maxPage - 1);
    setPagination(prev => ({ ...prev, page: clampedZeroBased + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10) || pagination.size;
    setPagination(prev => ({ ...prev, size: newSize, page: 1 }));
  };

useEffect(() => {
  const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
  if (loggedInUserData) {
    setCurrentUser(JSON.parse(loggedInUserData));
  }
}, []);
  // Fetch tasks
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get logged in user data from localStorage
      const loggedInUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      const userData = loggedInUserData ? JSON.parse(loggedInUserData) : null;
      const companyId = userData?.company_id;

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // First API call - Get company users
      const userParams = new URLSearchParams({
        page: '1',
        size: '10'
      });

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const usersResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.LIST_COMPANY_USERS(companyId)}?${userParams}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      );

      if (!usersResponse.ok) {
        throw new Error(`HTTP error! status: ${usersResponse.status}`);
      }

      const usersData = await usersResponse.json();
      // Correctly set users from the API response
      setUsers(usersData.items || []);

      // Second API call - Get tasks
      const taskParams = new URLSearchParams({
        page: pagination.page.toString(),
        size: pagination.size.toString(),
      });
      
      // Add filter parameters
      if (filters.assigned_to) taskParams.append('assigned_to', filters.assigned_to);
      if (filters.workspace_id) taskParams.append('workspace_id', filters.workspace_id);
      if (filters.channel_id) taskParams.append('channel_id', filters.channel_id);
      if (filters.thread_id) taskParams.append('thread_id', filters.thread_id);
      if (filters.source) taskParams.append('source', filters.source);
      if (filters.tags) taskParams.append('tags', filters.tags);

      const tasksResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?${taskParams}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      );

      if (!tasksResponse.ok) {
        throw new Error(`HTTP error! status: ${tasksResponse.status}`);
      }

      const tasksData: TasksResponse = await tasksResponse.json();
      const transformedTasks = transformTasksData(tasksData.tasks || []);
      setAllTasks(transformedTasks);
      setFilteredTasks(transformedTasks);
      setPagination(prev => ({
        ...prev,
        total: tasksData.total,
        page: tasksData.page,
        size: tasksData.size,
      }));

    } catch (err) {
      let errorMessage = 'Failed to fetch data';
      
      if (err instanceof Error) {
        if (err.message.includes('HTTP error! status:')) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : '';
          
          switch (statusCode) {
            case '401':
              markSessionExpired();
              return;
            case '403':
              errorMessage = 'You do not have permission to access this data.';
              break;
            case '500':
              errorMessage = 'Server error. Please try again later.';
              break;
            default:
              errorMessage = `Failed to load data (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes('Company ID not found')) {
          errorMessage = 'Company information not found. Please log in again.';
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [pagination.page, pagination.size]);

  // Helper functions for getting unique values (IDs + names) from all tasks
  const getUniqueCreators = () => {
    // Prefer using the first history user's id as created_by id fallback
    const map = new Map<string, string>();
    for (const task of allTasks) {
      const creatorName = task.task_details.created_by?.name;
      const creatorId = (task as any).task_details?.created_by?.id || task.task_history?.[0]?.user?.id;
      if (creatorId && creatorName && !map.has(creatorId)) {
        map.set(creatorId, creatorName);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  };

  const getUniqueAssignees = () => {
    const map = new Map<string, string>();
    for (const task of allTasks) {
      const assigneeId = task.task_details.assignee?.id;
      const assigneeName = task.task_details.assignee?.name;
      if (assigneeId && assigneeName && !map.has(assigneeId)) {
        map.set(assigneeId, assigneeName);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  };

  const getUniqueSpaces = () => {
    const map = new Map<string, string>();
    for (const task of allTasks) {
      const channelId = task.context.channel?.id as unknown as string;
      const channelName = task.context.channel?.name;
      if (channelId && channelName && !map.has(channelId)) {
        map.set(channelId, channelName);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  };

  // Local filtering is now handled by API calls
  // The filteredTasks will be set directly from API responses

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'priority') setPriorityFilterDisabled(Boolean(value));
    if (field === 'assigned_to') setAssigneeFilterDisabled(Boolean(value));
    
    // Trigger API call with updated filters
    fetchTasksWithFilters(field, value);
  };

  const fetchTasksWithFilters = async (field: string, value: string | string[]) => {
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!token) {
        console.error('No auth token found');
        setError('Authentication required');
        return;
      }

      // Update filters state first
      const updatedFilters = {
        ...filters,
        [field]: value
      };

      // Build API parameters
      const taskParams = new URLSearchParams({
        page: pagination.page.toString(),
        size: pagination.size.toString(),
      });
      
      // Add filter parameters based on API payload structure
      if (updatedFilters.status.length > 0) {
        updatedFilters.status.forEach(status => {
          taskParams.append('status', transformStatusForAPI(status));
        });
      }
      if (updatedFilters.priority) {
        taskParams.append('priority', updatedFilters.priority);
      }
      if (updatedFilters.assigned_to) {
        taskParams.append('assigned_to', updatedFilters.assigned_to);
      }
      if (updatedFilters.created_by) {
        taskParams.append('created_by', updatedFilters.created_by);
      }
      if (updatedFilters.workspace_id) {
        taskParams.append('workspace_id', updatedFilters.workspace_id);
      }
      if (updatedFilters.channel_id) {
        taskParams.append('channel_id', updatedFilters.channel_id);
      }
      if (updatedFilters.thread_id) {
        taskParams.append('thread_id', updatedFilters.thread_id);
      }
      if (updatedFilters.source) {
        taskParams.append('source', updatedFilters.source);
      }
      if (updatedFilters.tags) {
        taskParams.append('tags', updatedFilters.tags);
      }

      const tasksResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?${taskParams}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!tasksResponse.ok) {
        throw new Error(`HTTP error! status: ${tasksResponse.status}`);
      }

      const tasksData = await tasksResponse.json();
      const transformedTasks = transformTasksData(tasksData.tasks || []);
      setAllTasks(transformedTasks);
      setFilteredTasks(transformedTasks);
      setPagination(prev => ({
        ...prev,
        total: tasksData.total || 0
      }));
      setError(null);
    } catch (error) {
      console.error('Error fetching filtered tasks:', error);
      setError('Failed to fetch filtered tasks');
    } finally {
      setLoading(false);
    }
  };

  // Local filtering is now handled by API calls
  // The filteredTasks will be set directly from API responses

  const handleStatusToggle = (status: string) => {
    setFilters(prev => {
      const currentStatuses = prev.status;
      const isSelected = currentStatuses.includes(status);
      
      let newStatuses;
      if (isSelected) {
        // Remove status if already selected
        newStatuses = currentStatuses.filter(s => s !== status);
      } else {
        // Add status if not selected
        newStatuses = [...currentStatuses, status];
      }
      
      const updatedFilters = {
        ...prev,
        status: newStatuses
      };
      
      // Trigger API call with updated status filters
      fetchTasksWithStatusFilters(newStatuses);
      setStatusFilterDisabled(newStatuses.length > 0);
      
      return updatedFilters;
    });
  };

  const fetchTasksWithStatusFilters = async (statuses: string[]) => {
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (!token) {
        console.error('No auth token found');
        setError('Authentication required');
        return;
      }

      // Build API parameters
      const taskParams = new URLSearchParams({
        page: pagination.page.toString(),
        size: pagination.size.toString(),
      });
      
      // Add status filter parameters
      if (statuses.length > 0) {
        statuses.forEach(status => {
          taskParams.append('status', transformStatusForAPI(status));
        });
      }
      
      // Add other existing filters
      if (filters.priority) {
        taskParams.append('priority', filters.priority);
      }
      if (filters.assigned_to) {
        taskParams.append('assigned_to', filters.assigned_to);
      }
      if (filters.created_by) {
        taskParams.append('created_by', filters.created_by);
      }
      if (filters.workspace_id) {
        taskParams.append('workspace_id', filters.workspace_id);
      }
      if (filters.channel_id) {
        taskParams.append('channel_id', filters.channel_id);
      }
      if (filters.thread_id) {
        taskParams.append('thread_id', filters.thread_id);
      }
      if (filters.source) {
        taskParams.append('source', filters.source);
      }
      if (filters.tags) {
        taskParams.append('tags', filters.tags);
      }

      const tasksResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?${taskParams}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!tasksResponse.ok) {
        throw new Error(`HTTP error! status: ${tasksResponse.status}`);
      }

      const tasksData = await tasksResponse.json();
      const transformedTasks = transformTasksData(tasksData.tasks || []);
      setAllTasks(transformedTasks);
      setFilteredTasks(transformedTasks);
      setPagination(prev => ({
        ...prev,
        total: tasksData.total || 0
      }));
      setError(null);
    } catch (error) {
      console.error('Error fetching filtered tasks:', error);
      setError('Failed to fetch filtered tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllStatuses = () => {
    setFilters(prev => {
      // Clear status filters to show All
      const newStatuses: string[] = [];
      // Trigger API call with updated status filters
      fetchTasksWithStatusFilters(newStatuses);
      return {
        ...prev,
        status: newStatuses
      };
    });
    setStatusFilterAnchor(null);
  };

   const handleSort = (field: string) => {
     setSortConfig(prev => {
       if (prev.field === field) {
         // Same field, cycle through: null -> desc -> asc -> null
         if (prev.direction === null) {
           return { field, direction: 'desc' };
         } else if (prev.direction === 'desc') {
           return { field, direction: 'asc' };
         } else {
           return { field: '', direction: null };
         }
       } else {
         // New field, start with desc
         return { field, direction: 'desc' };
       }
     });

     // Client-side sort for created_at and due_date (no API call)
     if (field === 'created_at' || field === 'due_date') {
       setFilteredTasks(prev => {
         // Compute next direction based on current sortConfig
         const nextDirection = sortConfig.field === field
           ? (sortConfig.direction === null ? 'desc' : (sortConfig.direction === 'desc' ? 'asc' : null))
           : 'desc';

         if (nextDirection === null) {
           // Reset to original order
           return [...allTasks];
         }

         const sorted = [...prev].sort((a, b) => {
           const aTime = new Date((a.task_details as any)[field] || 0).getTime();
           const bTime = new Date((b.task_details as any)[field] || 0).getTime();
           return nextDirection === 'asc' ? aTime - bTime : bTime - aTime;
         });
         return sorted;
       });
     }
   };

      const getSortIcon = (field: string) => {
       if (sortConfig.field !== field) {
         // No sort applied - show default chevron down
         return <ExpandMoreIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
       }
       
       if (sortConfig.direction === 'desc') {
         // Descending - show chevron down
         return <ExpandMoreIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
       }
       
       if (sortConfig.direction === 'asc') {
         // Ascending - show chevron up
         return <ExpandLessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />;
       }
       
       return null;
     };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open':
        return 'warning';
      case 'In Progress':
        return 'info';
      case 'On Hold':
        return 'error';
      case 'Closed':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleStatusSelect = (taskId: string, status: string) => {
    // Close any previously expanded row and expand the new one
    setSelectedStatuses(prev => {
      const newStatuses = { ...prev };
      // Clear all previous selections
      Object.keys(newStatuses).forEach(key => {
        if (key !== taskId) {
          delete newStatuses[key];
        }
      });
      // Set the new selection
      newStatuses[taskId] = status;
      return newStatuses;
    });

    // Scroll to the expanded row after a short delay to ensure DOM is updated
    setTimeout(() => {
      const rowElement = document.querySelector(`[data-task-id="${taskId}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleCancel = (taskId: string) => {
    setRowComments(prev => ({ ...prev, [taskId]: '' }));
    setSelectedStatuses(prev => ({ ...prev, [taskId]: '' }));
    setRowAttachments(prev => ({ ...prev, [taskId]: [] }));
  };

  const handleFileUpload = (taskId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const currentAttachments = rowAttachments[taskId] || [];
    
    // Limit to 4 files total
    if (currentAttachments.length + files.length > 4) {
      alert('Maximum 4 files allowed');
      return;
    }
    
    setRowAttachments(prev => ({
      ...prev,
      [taskId]: [...currentAttachments, ...files]
    }));
  };

  const handleRemoveFile = (taskId: string, fileIndex: number) => {
    setRowAttachments(prev => ({
      ...prev,
      [taskId]: prev[taskId]?.filter((_, index) => index !== fileIndex) || []
    }));
  };

  const handleSubmit = async (taskId: string) => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.UPDATE_STATUS(taskId)}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          status: selectedStatuses[taskId],
          comments: rowComments[taskId] || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Close the expanded box after successful API call
      setTimeout(() => {
        setSelectedStatuses(prev => {
          const newStatuses = { ...prev };
          delete newStatuses[taskId];
          return newStatuses;
        });
        setRowComments(prev => {
          const newComments = { ...prev };
          delete newComments[taskId];
          return newComments;
        });
        setRowAttachments(prev => {
          const newAttachments = { ...prev };
          delete newAttachments[taskId];
          return newAttachments;
        });
      }, 1000);

      // Refresh the tasks list
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };


  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'success';
      case 'Medium':
        return 'info';
      case 'High':
        return 'warning';
      case 'Critical':
        return 'error';
      case 'Blocker':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdf sx={{ fontSize: 16, color: 'primary.main' }} />;
    if (['csv', 'xls', 'xlsx'].includes(ext || '')) return <InsertDriveFile sx={{ fontSize: 16, color: 'primary.main' }} />;
    if (['doc', 'docx'].includes(ext || '')) return <InsertDriveFile sx={{ fontSize: 16, color: 'primary.main' }} />;
    return <InsertDriveFile sx={{ fontSize: 16, color: 'primary.main' }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchTasks} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

           return (
      <Box sx={{ px: 1 }}>
             {/* Tasks Table */}
       <Paper sx={{ bgcolor: 'background.paper' }}>
        {/* Assignee Info Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2, 
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa'
        }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
        Assignee:
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  <Avatar 
    sx={{ width: 24, height: 24, bgcolor: getAvatarColor(selectedAssignee?.id || selectedAssignee?.name || currentUser?.id || currentUser?.name), fontSize: '12px', color: 'white' }}
  >
    {selectedAssignee?.name?.charAt(0) || currentUser?.name?.charAt(0) || ''}
  </Avatar>
  <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
    {selectedAssignee?.name || currentUser?.name || ''}
  </Typography>
  <IconButton 
  size="small" 
  sx={{ p: 0.5 }}
  onClick={(e) => {
    setAssigneeMenuAnchor(e.currentTarget);
    if (!assigneeMenuAnchor) {
      fetchUsers();
    }
  }}
>
  {assigneeMenuAnchor ? <ArrowUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> : <ArrowDownIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />}
</IconButton>
</Box>
</Box>
<Menu
  anchorEl={assigneeMenuAnchor}
  open={Boolean(assigneeMenuAnchor)}
  onClose={() => setAssigneeMenuAnchor(null)}
  anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left',
  }}
  transformOrigin={{
    vertical: 'top',
    horizontal: 'left',
  }}
  PaperProps={{
    sx: {
      minWidth: '200px',
      maxHeight: '300px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  }}
>
  {/* Search Field */}
  <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
    <TextField
      fullWidth
      size="small"
      placeholder="Search users..."
      value={userSearchQuery}
      onChange={(e) => setUserSearchQuery(e.target.value)}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, height: '40px' } }}
    />
  </Box>
  {loadingUsers ? (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={20} />
    </Box>
  ) : filteredUsers.length === 0 ? (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {userSearchQuery ? 'No users found matching your search' : 'No users found'}
      </Typography>
    </Box>
  ) : (
    filteredUsers.map((user, index) => (
      <MenuItem
        key={user.id || index}
        onClick={() => {
          // reflect selection in header and set filter id
          setSelectedAssignee({ name: user.name, email: user.email_id, id: user.id });
          handleFilterChange('assigned_to', user.id);
          setAssigneeMenuAnchor(null);
        }}
        sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: index < filteredUsers.length - 1 ? 1 : 0, borderColor: 'divider' }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: getAvatarColor(user.id || user.name), fontSize: '14px', color: 'white' }}>
          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email_id}
          </Typography>
        </Box>
      </MenuItem>
    ))
  )}
</Menu>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={(e) => setTimeFilterMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CalendarIcon fontSize="small" />
              {filters.timeFilter}
              <ArrowDownIcon fontSize="small" />
            </Button>
          </Box>
        </Box>

                                                                       <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 240px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%',
            '& .MuiTable-root': {
              width: '100%'
            }
          }}>
                            <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
               <TableRow sx={{ bgcolor: 'background.paper' }}>
                                                                                                                                                                                                                                                                 <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        width: '15%',
                        pr: 1.5
                      }}>
                        Name
                      </TableCell>
                                       <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        width: '12%',
                        pr: 1.5
                      }}>
                        Description
                      </TableCell>
                                                                          <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        width: '12%',
                        pr: 1.5
                      }}>
                        Status
                       <IconButton 
                         size="small" 
                         sx={{ ml: -0.25, mt: 0.5 }}
                         onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                         title="Filter by Status"
                       >
                         <FilterListIcon fontSize="small" sx={{ color: filters.status.length > 0 ? '#1976d2' : '#9e9e9e' }} />
                       </IconButton>
                                                {filters.status.length > 0 && (
                          <IconButton size="small" onClick={() => { handleFilterChange('status', []); setStatusFilterDisabled(false); setStatusFilterAnchor(null)}} sx={{ ml: -0.25, mt: 0.5 }} title="Clear Status Filter">
                           <Box sx={{ position: 'relative' }}>
                             <FilterListIcon fontSize="small" sx={{ color: '#9e9e9e', opacity: 0.7 }} />
                             <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '80%', height: '2px', backgroundColor: '#9e9e9e', transform: 'translateX(-50%) rotate(-45deg)', opacity: 0.8 }} />
                           </Box>
                         </IconButton>
                       )}
                     </TableCell>
                                      <TableCell sx={{ 
                      fontWeight: 'bold', 
                      color: 'primary.main',
                      whiteSpace: 'nowrap',
                      width: '12%',
                      pr: 1.5
                    }}>
                                          Created By
                       <IconButton 
                         size="small" 
                         sx={{ ml: -0.25, mt: 0.5 }}
                         onClick={(e) => setCreatedByFilterAnchor(e.currentTarget)}
                         title="Filter by Created By"
                       >
                         <FilterListIcon fontSize="small" sx={{ color: filters.created_by ? '#1976d2' : '#9e9e9e' }} />
                       </IconButton>
                                                {filters.created_by && (
                          <IconButton size="small" onClick={() => { handleFilterChange('created_by', ''); setPriorityFilterDisabled(false); }} sx={{ ml: -0.25, mt: 0.5 }} title="Clear Created By Filter">
                           <Box sx={{ position: 'relative' }}>
                             <FilterListIcon fontSize="small" sx={{ color: '#9e9e9e', opacity: 0.7 }} />
                             <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '80%', height: '2px', backgroundColor: '#9e9e9e', transform: 'translateX(-50%) rotate(-45deg)', opacity: 0.8 }} />
                           </Box>
                         </IconButton>
                       )}
                    </TableCell>
                                                                          <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'primary.main',
                        whiteSpace: 'nowrap',
                        width: '10%',
                        pr: 1.5
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Assignee
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={(e) => setAssigneeFilterAnchor(e.currentTarget)}
                            disabled={assigneeFilterDisabled}
                            sx={{ ml: -0.25, mt: 0.5 }}
                            title="Filter by Assignee"
                          >
                            <FilterListIcon fontSize="small" sx={{ color: filters.assigned_to ? '#1976d2' : '#9e9e9e' }} />
                          </IconButton>
                                                      {filters.assigned_to && (
                             <IconButton size="small" onClick={() => { handleFilterChange('assigned_to', ''); setAssigneeFilterDisabled(false); setSelectedAssignee(null); }} sx={{ ml: -0.25, mt: 0.5 }} title="Clear Assignee Filter">
                              <Box sx={{ position: 'relative' }}>
                                <FilterListIcon fontSize="small" sx={{ color: '#9e9e9e', opacity: 0.7 }} />
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '80%', height: '2px', backgroundColor: '#9e9e9e', transform: 'translateX(-50%) rotate(-45deg)', opacity: 0.8 }} />
                              </Box>
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                                                                          <TableCell sx={{ 
                        fontWeight: 'bold', 
                        color: 'primary.main',
                        width: '10%',
                        pr: 1.5
                      }}>
                                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                           <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                             Created Date
                           </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleSort('created_at')}
                            sx={{ p: 0.5 }}
                          >
                            {getSortIcon('created_at')}
                          </IconButton>
                        </Box>
                      </TableCell>
                                      <TableCell sx={{ 
                       fontWeight: 'bold', 
                       color: 'primary.main',
                       whiteSpace: 'nowrap',
                       width: '10%',
                       pr: 1.5
                     }}>
                       Priority
                       <IconButton 
                         size="small" 
                         sx={{ ml: -0.25, mt: 0.5 }}
                         ref={priorityFilterBtnRef}
                         onClick={() => { setPriorityFilterAnchor(priorityFilterBtnRef.current); setPriorityMenuOpen(true); }}
                         disabled={priorityFilterDisabled}
                       >
                         <FilterListIcon fontSize="small" sx={{ color: filters.priority ? '#1976d2' : '#9e9e9e' }} />
                       </IconButton>
                                                {filters.priority && (
                          <IconButton size="small" onClick={() => { handleFilterChange('priority', ''); setPriorityFilterDisabled(false); setPriorityFilterAnchor(null)}} sx={{ ml: -0.25, mt: 0.5 }} title="Clear Priority Filter">
                           <Box sx={{ position: 'relative' }}>
                             <FilterListIcon fontSize="small" sx={{ color: '#9e9e9e', opacity: 0.7 }} />
                             <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '80%', height: '2px', backgroundColor: '#9e9e9e', transform: 'translateX(-50%) rotate(-45deg)', opacity: 0.8 }} />
                           </Box>
                         </IconButton>
                       )}
                     </TableCell>
                                                                                               <TableCell sx={{ 
                         fontWeight: 'bold', 
                         color: 'primary.main',
                         whiteSpace: 'nowrap',
                         width: '10%',
                         pr: 1.5
                       }}>
                         {LanguageConstants.topic}
                         <IconButton 
                           size="small" 
                           sx={{ ml: -0.25, mt: 0.5 }}
                           onClick={(e) => setSpaceFilterAnchor(e.currentTarget)}
                           title={`Filter by ${LanguageConstants.topic}`}
                         >
                           <FilterListIcon fontSize="small" sx={{ color: filters.channel_id ? '#1976d2' : '#9e9e9e' }} />
                         </IconButton>
                                                    {filters.channel_id && (
                            <IconButton size="small" onClick={() => handleFilterChange('channel_id', '')} sx={{ ml: -0.25, mt: 0.5 }} title={`Clear ${LanguageConstants.topic} Filter`}>
                             <Box sx={{ position: 'relative' }}>
                               <FilterListIcon fontSize="small" sx={{ color: '#9e9e9e', opacity: 0.7 }} />
                               <Box sx={{ position: 'absolute', top: '50%', left: '50%', width: '80%', height: '2px', backgroundColor: '#9e9e9e', transform: 'translateX(-50%) rotate(-45deg)', opacity: 0.8 }} />
                             </Box>
                           </IconButton>
                         )}
                       </TableCell>
                                                                           <TableCell sx={{ 
                       fontWeight: 'bold', 
                       color: 'primary.main',
                       whiteSpace: 'nowrap',
                       width: '9%',
                       pr: 1.5
                     }}>
                       Due Date
                       <IconButton 
                         size="small" 
                         sx={{ ml: -1 }}
                         onClick={() => handleSort('due_date')}
                       >
                         {getSortIcon('due_date')}
                       </IconButton>
                     </TableCell>
               </TableRow>
             </TableHead>
            <TableBody>
                             {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No tasks found
                    </Typography>
                  </TableCell>
                </TableRow>
                                             ) : (
                  filteredTasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                      <TableRow 
                        hover
                        data-task-id={task.id}
                        onClick={() => handleTaskRowClick(task)}
                        sx={{ 
                          backgroundColor: index % 2 === 0
                            ? (isDarkMode ? 'grey.800' : '#E0F7FA')
                            : (isDarkMode ? 'grey.900' : '#fff'),
                          border: expandedRowId === task.id ? '2px solid' : 'none',
                          borderColor: expandedRowId === task.id ? 'primary.main' : 'transparent',
                          cursor: task.context?.channel?.id && task.context?.thread?.id ? 'pointer' : 'default'
                        }}
                      >
                                                                                                                                                                                                                                                                                       <TableCell 
                             sx={{ 
                               whiteSpace: 'nowrap',
                               width: '15%',
                               overflow: 'hidden',
                               pr: 1.5
                             }}
                             title={task.task_details.task_name}
                           >
                             <Typography variant="body2" sx={{ 
                               fontWeight: 'bold',
                               overflow: 'hidden',
                               textOverflow: 'ellipsis',
                               whiteSpace: 'nowrap'
                             }}>
                               {task.task_details.task_name}
                             </Typography>
                           </TableCell>
                           <TableCell 
                             sx={{ 
                               whiteSpace: 'nowrap',
                               width: '12%',
                               overflow: 'hidden',
                               pr: 1.5
                             }}
                             title={task.task_details.description || '-'}
                           >
                            <Typography variant="body2" color="text.secondary" sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {task.task_details.description || '-'}
                            </Typography>
                          </TableCell>
                                                                                                                                                                                 <TableCell 
                            sx={{ 
                              whiteSpace: 'nowrap',
                              width: '12%',
                              pr: 1.5
                            }}
                            title={task.task_details.status}
                          >
                                                                                                                            <Button
                             variant="outlined"
                             size="small"
                             onClick={(e) => {
                               e.stopPropagation(); // Prevent row click redirect when using status dropdown
                               if (task.task_details.status !== 'Closed') {
                                 setStatusDropdownAnchor(e.currentTarget);
                                 setSelectedTaskId(task.id);
                               }
                             }}
                             sx={{
                               borderRadius: '16px',
                               textTransform: 'none',
                               border: 'none',
                               color: task.task_details.status === 'In Progress' ? (isDarkMode ? '#90caf9' : '#1976d2') :
                                       task.task_details.status === 'On Hold' ? '#ef5350' :
                                       task.task_details.status === 'Closed' ? (isDarkMode ? '#81c784' : 'rgb(0, 128, 0)') : 'text.secondary',
                               backgroundColor: task.task_details.status === 'In Progress' ? (isDarkMode ? 'grey.700' : '#e3f2fd') :
                                              task.task_details.status === 'On Hold' ? (isDarkMode ? 'grey.700' : '#ffebee') :
                                              task.task_details.status === 'Closed' ? (isDarkMode ? 'grey.700' : 'rgb(204, 230, 204)') : (isDarkMode ? 'grey.700' : '#f5f5f5'),
                               cursor: task.task_details.status === 'Closed' ? 'default' : 'pointer',
                               px: 2,
                               py: 0.5,
                               fontWeight: 'medium',
                                                               '&:hover': {
                                  backgroundColor: task.task_details.status === 'In Progress' ? (isDarkMode ? 'grey.600' : '#bbdefb') :
                                                 task.task_details.status === 'On Hold' ? (isDarkMode ? 'grey.600' : '#ffcdd2') :
                                                 task.task_details.status === 'Closed' ? (isDarkMode ? 'grey.700' : 'rgb(204, 230, 204)') : (isDarkMode ? 'grey.600' : '#e0e0e0')
                                }
                             }}
                           >
                             {task.task_details.status}
                             {task.task_details.status !== 'Closed' && (
                               <ArrowDownIcon fontSize="small" sx={{ ml: 0.5, color: 'inherit' }} />
                             )}
                           </Button>
                      </TableCell>
                                                                                                                                                                                                                                                                                                                                                                               <TableCell 
                            sx={{ 
                              width: '10%',
                              overflow: 'hidden',
                              pr: 1.5
                            }}
                            title={task.task_details.created_by?.name || 'Google'}
                          >
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                             <Avatar 
                               sx={{ width: 24, height: 24, bgcolor: getAvatarColor(task.task_details.created_by?.name), fontSize: '10px', flexShrink: 0 }}
                               src={task.task_history[0]?.user?.avatar_url || undefined}
                             >
                               {task.task_details.created_by?.name.charAt(0) || 'G'}
                             </Avatar>
                             <Typography 
                               variant="body2"
                               sx={{
                                 whiteSpace: 'nowrap',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 minWidth: 0,
                                 flex: 1
                               }}
                             >
                               {task.task_details.created_by?.name || 'Google'}
                             </Typography>
                           </Box>
                         </TableCell>
                                                                                                   <TableCell 
                             sx={{ 
                               width: '10%',
                               overflow: 'hidden',
                               pr: 1.5
                             }}
                             title={task.task_details.assignee?.name || 'Karthick'}
                           >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Avatar 
                                sx={{ width: 24, height: 24, bgcolor: getAvatarColor(task.task_details.assignee?.id || task.task_details.assignee?.name), fontSize: '10px', flexShrink: 0 }}
                                src={task.task_details.assignee?.avatar_url || undefined}
                              >
                                {task.task_details.assignee?.name?.charAt(0) || 'K'}
                              </Avatar>
                              <Typography 
                                variant="body2"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minWidth: 0,
                                  flex: 1
                                }}
                              >
                                {task.task_details.assignee?.name || 'Karthick'}
                              </Typography>
                            </Box>
                          </TableCell>
                                                                                                                                                                                                                                                                       <TableCell 
                            sx={{ 
                              whiteSpace: 'nowrap',
                              width: '10%',
                              overflow: 'hidden',
                              pr: 1.5
                            }}
                            title={new Date(task.task_details.created_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          >
                           <Typography variant="body2" sx={{
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}>
                             {new Date(task.task_details.created_at).toLocaleDateString('en-GB', {
                               day: '2-digit',
                               month: 'short',
                               year: 'numeric'
                             })}
                           </Typography>
                         </TableCell>
                                                   <TableCell 
                            sx={{ 
                              whiteSpace: 'nowrap',
                              width: '10%',
                              overflow: 'hidden',
                              pr: 1.5
                            }}
                            title={task.task_details.priority || 'Medium'}
                          >
                           {(() => {
                             const priorityDisplay = getPriorityDisplay(task.task_details.priority || 'Medium');
                             return (
                               <Box sx={{ 
                                 display: 'flex', 
                                 alignItems: 'center', 
                                 gap: 0.5
                               }}>
                                 <Box sx={{ color: priorityDisplay.color }}>
                                   {priorityDisplay.icon}
                                 </Box>
                           <Typography variant="body2" sx={{ 
                                   color: priorityDisplay.color,
                                   fontWeight: 'medium',
                             overflow: 'hidden',
                             textOverflow: 'ellipsis',
                             whiteSpace: 'nowrap'
                           }}>
                                   {task.task_details.priority || 'Medium'}
                           </Typography>
                               </Box>
                             );
                           })()}
                         </TableCell>
                                                   <TableCell 
                            sx={{ 
                              width: '10%',
                              overflow: 'hidden',
                              pr: 1.5
                            }}
                            title={task.context.channel?.name || `No ${LanguageConstants.topic}`}
                          >
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                             <GroupIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                             <Typography 
                               variant="body2" 
                               sx={{ 
                                 color: 'primary.main',
                                 whiteSpace: 'nowrap',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 minWidth: 0,
                                 flex: 1
                               }}
                             >
                               {task.context.channel?.name || `No ${LanguageConstants.topic}`}
                             </Typography>
                           </Box>
                         </TableCell>
                                                                                                   <TableCell 
                            sx={{ 
                              whiteSpace: 'nowrap',
                              width: '9%',
                              overflow: 'hidden',
                              pr: 1.5
                            }}
                           title={task.task_details.due_date ? (
                            (() => {
                              const dueDate = new Date(task.task_details.due_date);
                              const today = new Date();
                              const diffTime = dueDate.getTime() - today.getTime();
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                              
                              if (diffDays < 0) {
                                return `Expired ${Math.abs(diffDays)} days ago`;
                              } else if (diffDays === 0) {
                                return 'Due today';
                              } else if (diffDays === 1) {
                                return 'Due tomorrow';
                              } else {
                                return `Due in ${diffDays} days`;
                              }
                            })()
                          ) : (
                            'No due date'
                          )}
                        >
                         <Typography variant="body2" color="error" sx={{
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap'
                         }}>
                           {task.task_details.due_date ? (
                             (() => {
                               const dueDate = new Date(task.task_details.due_date);
                               const today = new Date();
                               const diffTime = dueDate.getTime() - today.getTime();
                               const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                               
                               if (diffDays < 0) {
                                 return `Expired ${Math.abs(diffDays)} days ago`;
                               } else if (diffDays === 0) {
                                 return 'Due today';
                               } else if (diffDays === 1) {
                                 return 'Due tomorrow';
                               } else {
                                 return `Due in ${diffDays} days`;
                               }
                             })()
                           ) : (
                             'No due date'
                           )}
                         </Typography>
                                               </TableCell>
                      </TableRow>
                      
                                             {/* Expanded Row for Status Change */}
                       {selectedStatuses[task.id] && (
                         <TableRow>
                           <TableCell colSpan={9} sx={{ p: 0, border: 'none', width: '100%' }}>
                             <Box sx={{ 
                               p: 1.5, 
                               backgroundColor: isDarkMode ? 'grey.800' : '#f8f9fa',
                               borderTop: 1,
                               borderColor: 'divider',
                               width: '100%',
                               boxSizing: 'border-box'
                             }}>
                              {/* Status Change Prompt */}
                                                             <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 'medium' }}>
                                 Change task status to <span style={{ 
                                   color: selectedStatuses[task.id] === 'On Hold' ? '#d32f2f' : 
                                          selectedStatuses[task.id] === 'Closed' ? 'rgb(0, 128, 0)' : '#1976d2', 
                                   fontWeight: 'bold' 
                                 }}>{selectedStatuses[task.id]}</span>
                               </Typography>
                              
                              {/* Comment Section */}
                              <Box sx={{ mb: 1.5 }}>
                                                                 <TextField
                                   multiline
                                   rows={2}
                                   placeholder="Add comment .."
                                   value={rowComments[task.id] || ''}
                                   onChange={(e) => setRowComments(prev => ({ ...prev, [task.id]: e.target.value }))}
                                   fullWidth
                                   variant="outlined"
                                   size="small"
                                   sx={{
                                     '& .MuiOutlinedInput-root': {
                                       borderRadius: '6px',
                                       backgroundColor: isDarkMode ? 'grey.900' : '#fff'
                                     }
                                   }}
                                 />
                              </Box>
                              
                                                                                                                           {/* File List - Above attachment icon and buttons */}
                               {rowAttachments[task.id] && rowAttachments[task.id].length > 0 && (
                                 <Box sx={{ mb: 1.5 }}>
                                   <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                     {rowAttachments[task.id].map((file, index) => (
                                                                               <Chip
                                          key={index}
                                          icon={getFileIcon(file.name)}
                                          label={file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name}
                                          onDelete={() => handleRemoveFile(task.id, index)}
                                          deleteIcon={<CloseIcon />}
                                          sx={{
                                            backgroundColor: isDarkMode ? 'grey.700' : '#f3f4f6',
                                            color: isDarkMode ? 'grey.200' : '#374151',
                                            fontSize: '0.75rem',
                                            borderRadius: '6px',
                                            border: 1,
                                            borderColor: isDarkMode ? 'grey.600' : '#e5e7eb',
                                            '& .MuiChip-icon': {
                                              color: 'primary.main',
                                              margin: '0 4px 0 0'
                                            },
                                            '& .MuiChip-deleteIcon': {
                                              color: 'primary.main',
                                              '&:hover': {
                                                color: '#d32f2f'
                                              }
                                            }
                                          }}
                                        />
                                     ))}
                                   </Box>
                                 </Box>
                               )}

                               {/* Attachment Section and Buttons in Single Row */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <input
                                      type="file"
                                      multiple
                                      style={{ display: 'none' }}
                                      id={`file-upload-${task.id}`}
                                      onChange={(e) => handleFileUpload(task.id, e)}
                                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                                    />
                                                                        <label htmlFor={`file-upload-${task.id}`}>
                                       {/* <Box
                                         sx={{
                                           position: 'relative',
                                           display: 'inline-flex',
                                           alignItems: 'center',
                                           justifyContent: 'center',
                                           width: 28,
                                           height: 28,
                                           borderRadius: '50%',
                                           border: '1px solid #d1d5db',
                                           backgroundColor: '#fff',
                                           cursor: 'pointer',
                                           transition: 'all 0.2s ease',
                                           '&:hover': {
                                             borderColor: '#9ca3af',
                                             backgroundColor: '#f9fafb'
                                           }
                                         }}
                                       >
                                         <AttachFileIcon sx={{ color: '#666', fontSize: '16px' }} />
                                         {rowAttachments[task.id] && rowAttachments[task.id].length > 0 && (
                                           <Box
                                             sx={{
                                               position: 'absolute',
                                               top: -6,
                                               right: -6,
                                               width: 18,
                                               height: 18,
                                               borderRadius: '50%',
                                               backgroundColor: '#1976d2',
                                               display: 'flex',
                                               alignItems: 'center',
                                               justifyContent: 'center',
                                               border: '2px solid',
                                             borderColor: 'background.paper'
                                             }}
                                           >
                                             <Typography
                                               variant="caption"
                                               sx={{
                                                 color: '#fff',
                                                 fontSize: '0.65rem',
                                                 fontWeight: 'bold',
                                                 lineHeight: 1
                                               }}
                                             >
                                               {rowAttachments[task.id].length}
                                             </Typography>
                                           </Box>
                                         )}
                                       </Box> */}
                                     </label>
                                  </Box>
                                  
                                  {/* Submit/Cancel Buttons */}
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleCancel(task.id)}
                                      sx={{
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        px: 1.5,
                                        py: 0.5
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleSubmit(task.id)}
                                      disabled={!rowComments[task.id]?.trim()}
                                      sx={{
                                        backgroundColor: '#1976d2',
                                        fontSize: '0.75rem',
                                        px: 1.5,
                                        py: 0.5,
                                        '&:hover': { backgroundColor: '#1565c0' }
                                      }}
                                    >
                                      Submit
                                    </Button>
                                  </Box>
                                </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
          </Table>
        </TableContainer>
                 <TablePagination
           component="div"
           count={pagination.total}
           page={Math.max(0, pagination.page - 1)}
           onPageChange={handleChangePage}
           rowsPerPage={pagination.size}
           onRowsPerPageChange={handleChangeRowsPerPage}
           rowsPerPageOptions={[10, 20, 50]}
         />
       </Paper>

               {/* Status Filter Menu */}
        <Menu
          anchorEl={statusFilterAnchor}
          open={Boolean(statusFilterAnchor) && !statusFilterDisabled}
          onClose={() => setStatusFilterAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          // Ensure the menu stays within viewport and below the icon
          disablePortal
          keepMounted
        >
                     <MenuItem onClick={handleSelectAllStatuses}>
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <Box
                 sx={{
                   width: 16,
                   height: 16,
                   border: '1px solid #ccc',
                   borderRadius: '2px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: filters.status.length === 0 ? '#1976d2' : 'transparent'
                 }}
               >
                 {filters.status.length === 0 && (
                   <Box
                     sx={{
                       width: 10,
                       height: 6,
                       borderLeft: '2px solid white',
                       borderBottom: '2px solid white',
                       transform: 'rotate(-45deg)',
                       marginTop: '-1px'
                     }}
                   />
                 )}
               </Box>
               All
             </Box>
           </MenuItem>
          <MenuItem onClick={() => handleStatusToggle('Open')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: filters.status.includes('Open') ? '#1976d2' : 'transparent'
                }}
              >
                {filters.status.includes('Open') && (
                  <Box
                    sx={{
                      width: 10,
                      height: 6,
                      borderLeft: '2px solid white',
                      borderBottom: '2px solid white',
                      transform: 'rotate(-45deg)',
                      marginTop: '-1px'
                    }}
                  />
                )}
              </Box>
              Open
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusToggle('In Progress')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: filters.status.includes('In Progress') ? '#1976d2' : 'transparent'
                }}
              >
                {filters.status.includes('In Progress') && (
                  <Box
                    sx={{
                      width: 10,
                      height: 6,
                      borderLeft: '2px solid white',
                      borderBottom: '2px solid white',
                      transform: 'rotate(-45deg)',
                      marginTop: '-1px'
                    }}
                  />
                )}
              </Box>
              In Progress
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusToggle('On Hold')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: filters.status.includes('On Hold') ? '#1976d2' : 'transparent'
                }}
              >
                {filters.status.includes('On Hold') && (
                  <Box
                    sx={{
                      width: 10,
                      height: 6,
                      borderLeft: '2px solid white',
                      borderBottom: '2px solid white',
                      transform: 'rotate(-45deg)',
                      marginTop: '-1px'
                    }}
                  />
                )}
              </Box>
              On Hold
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusToggle('Closed')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid #ccc',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: filters.status.includes('Closed') ? '#1976d2' : 'transparent'
                }}
              >
                {filters.status.includes('Closed') && (
                  <Box
                    sx={{
                      width: 10,
                      height: 6,
                      borderLeft: '2px solid white',
                      borderBottom: '2px solid white',
                      transform: 'rotate(-45deg)',
                      marginTop: '-1px'
                    }}
                  />
                )}
              </Box>
              Closed
            </Box>
          </MenuItem>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <MenuItem onClick={() => setStatusFilterAnchor(null)}>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                Close
              </Typography>
            </MenuItem>
          </Box>
        </Menu>

               {/* Priority Filter Menu */}
        <Menu
          anchorEl={priorityFilterAnchor}
          open={Boolean(priorityFilterAnchor) && !priorityFilterDisabled}
          onClose={() => setPriorityFilterAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disablePortal
          keepMounted
        >
          
           <MenuItem onClick={() => {
             handleFilterChange('priority', 'Low');
           }}>
             Low
           </MenuItem>
           <MenuItem onClick={() => {
             handleFilterChange('priority', 'Medium');
           }}>
             Medium
           </MenuItem>
           <MenuItem onClick={() => {
             handleFilterChange('priority', 'High');
           }}>
             High
           </MenuItem>
           <MenuItem onClick={() => {
             handleFilterChange('priority', 'Critical');
           }}>
             Critical
           </MenuItem>
           <MenuItem onClick={() => {
             handleFilterChange('priority', 'Blocker');
           }}>
             Blocker
           </MenuItem>
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <MenuItem onClick={() => setPriorityFilterAnchor(null)}>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                Close
              </Typography>
            </MenuItem>
          </Box>
        </Menu>

                 {/* Assignee Filter Menu */}
         <Menu
           anchorEl={assigneeFilterAnchor}
           open={Boolean(assigneeFilterAnchor) && !assigneeFilterDisabled}
           onClose={() => setAssigneeFilterAnchor(null)}
           anchorOrigin={{
             vertical: 'bottom',
             horizontal: 'left',
           }}
           transformOrigin={{
             vertical: 'top',
             horizontal: 'left',
           }}
           PaperProps={{
             sx: {
               minWidth: '200px',
               maxHeight: '300px',
               borderRadius: '8px',
               boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
             }
           }}
           disablePortal
           keepMounted
         >
            <MenuItem 
              onClick={() => {
                handleFilterChange('assigned_to', '');
                setAssigneeFilterAnchor(null);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: !filters.assigned_to ? '#1976d2' : 'transparent'
                  }}
                >
                  {!filters.assigned_to && (
                    <Box
                      sx={{
                        width: 10,
                        height: 6,
                        borderLeft: '2px solid white',
                        borderBottom: '2px solid white',
                        transform: 'rotate(-45deg)',
                        marginTop: '-1px'
                      }}
                    />
                  )}
                </Box>
                All
              </Box>
            </MenuItem>
            {getUniqueAssignees().map((assignee) => (
              <MenuItem 
                key={assignee.id}
                onClick={() => {
                  handleFilterChange('assigned_to', filters.assigned_to === assignee.id ? '' : assignee.id);
                  setAssigneeFilterAnchor(null);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: filters.assigned_to === assignee.id ? '#1976d2' : 'transparent'
                    }}
                  >
                    {filters.assigned_to === assignee.id && (
                      <Box
                        sx={{
                          width: 10,
                          height: 6,
                          borderLeft: '2px solid white',
                          borderBottom: '2px solid white',
                          transform: 'rotate(-45deg)',
                          marginTop: '-1px'
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      sx={{ width: 24, height: 24, bgcolor: getAvatarColor(assignee.id || assignee.name), fontSize: '12px' }}
                    >
                      {assignee.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">{assignee.name}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
            <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1, mt: 1 }}>
              <MenuItem onClick={() => setAssigneeFilterAnchor(null)}>
                <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                  Close
                </Typography>
              </MenuItem>
            </Box>
         </Menu>

         {/* Created By Filter Menu */}
         <Menu
           anchorEl={createdByFilterAnchor}
           open={Boolean(createdByFilterAnchor)}
           onClose={() => setCreatedByFilterAnchor(null)}
           anchorOrigin={{
             vertical: 'bottom',
             horizontal: 'left',
           }}
           transformOrigin={{
             vertical: 'top',
             horizontal: 'left',
           }}
           PaperProps={{
             sx: {
               minWidth: '200px',
               maxHeight: '300px',
               borderRadius: '8px',
               boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
             }
           }}
         >
           <MenuItem 
             onClick={() => {
               handleFilterChange('created_by', '');
               setCreatedByFilterAnchor(null);
             }}
           >
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <Box
                 sx={{
                   width: 16,
                   height: 16,
                   border: '1px solid #ccc',
                   borderRadius: '2px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: !filters.created_by ? '#1976d2' : 'transparent'
                 }}
               >
                 {!filters.created_by && (
                   <Box
                     sx={{
                       width: 8,
                       height: 8,
                       backgroundColor: 'white',
                       borderRadius: '1px'
                     }}
                   />
                 )}
               </Box>
               All
             </Box>
           </MenuItem>
           {getUniqueCreators().map((creator) => (
             <MenuItem 
               key={creator.id}
               onClick={() => {
                 handleFilterChange('created_by', filters.created_by === creator.id ? '' : creator.id);
                 setCreatedByFilterAnchor(null);
               }}
             >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Box
                   sx={{
                     width: 16,
                     height: 16,
                     border: '1px solid #ccc',
                     borderRadius: '2px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: filters.created_by === creator.id ? '#1976d2' : 'transparent'
                   }}
                 >
                   {filters.created_by === creator.id && (
                     <Box
                       sx={{
                         width: 8,
                         height: 8,
                         backgroundColor: 'white',
                         borderRadius: '1px'
                       }}
                     />
                   )}
                 </Box>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <Avatar 
                     sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '12px' }}
                   >
                     {creator.name.charAt(0).toUpperCase()}
                   </Avatar>
                   <Typography variant="body2">{creator.name}</Typography>
                 </Box>
               </Box>
             </MenuItem>
           ))}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <MenuItem onClick={() => setCreatedByFilterAnchor(null)}>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                Close
              </Typography>
            </MenuItem>
          </Box>
        </Menu>

         {/* Space Filter Menu */}
         <Menu
           anchorEl={spaceFilterAnchor}
           open={Boolean(spaceFilterAnchor)}
           onClose={() => setSpaceFilterAnchor(null)}
           anchorOrigin={{
             vertical: 'bottom',
             horizontal: 'left',
           }}
           transformOrigin={{
             vertical: 'top',
             horizontal: 'left',
           }}
           PaperProps={{
             sx: {
               minWidth: '200px',
               maxHeight: '300px',
               borderRadius: '8px',
               boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
             }
           }}
         >
           <MenuItem 
             onClick={() => {
               handleFilterChange('channel_id', '');
               setSpaceFilterAnchor(null);
             }}
           >
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <Box
                 sx={{
                   width: 16,
                   height: 16,
                   border: '1px solid #ccc',
                   borderRadius: '2px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   backgroundColor: !filters.channel_id ? '#1976d2' : 'transparent'
                 }}
               >
                 {!filters.channel_id && (
                   <Box
                     sx={{
                       width: 10,
                       height: 6,
                       borderLeft: '2px solid white',
                       borderBottom: '2px solid white',
                       transform: 'rotate(-45deg)',
                       marginTop: '-1px'
                     }}
                   />
                 )}
               </Box>
               All
             </Box>
           </MenuItem>
           {getUniqueSpaces().map((space) => (
             <MenuItem 
               key={space.id}
               onClick={() => {
                 handleFilterChange('channel_id', filters.channel_id === space.id ? '' : space.id);
                 setSpaceFilterAnchor(null);
               }}
             >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Box
                   sx={{
                     width: 16,
                     height: 16,
                     border: '1px solid #ccc',
                     borderRadius: '2px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     backgroundColor: filters.channel_id === space.id ? '#1976d2' : 'transparent'
                   }}
                 >
                   {filters.channel_id === space.id && (
                     <Box
                       sx={{
                         width: 10,
                         height: 6,
                         borderLeft: '2px solid white',
                         borderBottom: '2px solid white',
                         transform: 'rotate(-45deg)',
                         marginTop: '-1px'
                       }}
                     />
                   )}
                 </Box>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                   <GroupIcon sx={{ width: 20, height: 20, color: 'primary.main' }} />
                   <Typography variant="body2">{space.name}</Typography>
                 </Box>
               </Box>
             </MenuItem>
           ))}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
            <MenuItem onClick={() => setSpaceFilterAnchor(null)}>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                Close
              </Typography>
            </MenuItem>
          </Box>
        </Menu>

                   {/* Status Dropdown Menu for Table Rows */}
          <Menu
            anchorEl={statusDropdownAnchor}
            open={Boolean(statusDropdownAnchor)}
            onClose={() => {
              setStatusDropdownAnchor(null);
              setSelectedTaskId(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                minWidth: '150px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            {(() => {
              const currentTask = allTasks.find(task => task.id === selectedTaskId);
              const currentStatus = currentTask?.task_details.status || 'Open';
              
              // Don't show dropdown for "Closed" status
              if (currentStatus === 'Closed') {
                return null;
              }
              
                             // Show different options based on current status (Menu requires array, not Fragment)
               if (currentStatus === 'Open') {
                 return [
                   <MenuItem
                     key="in-progress"
                     onClick={() => {
                       if (selectedTaskId) {
                         handleStatusSelect(selectedTaskId, 'In Progress');
                       }
                       setStatusDropdownAnchor(null);
                       setSelectedTaskId(null);
                     }}
                     sx={{
                       backgroundColor: isDarkMode ? 'grey.700' : '#e3f2fd',
                       color: isDarkMode ? '#90caf9' : '#1976d2',
                       fontWeight: 'medium',
                       '&:hover': {
                         backgroundColor: isDarkMode ? 'grey.600' : '#bbdefb'
                       }
                     }}
                   >
                     In Progress
                   </MenuItem>,
                   <MenuItem
                     key="on-hold"
                     onClick={() => {
                       if (selectedTaskId) {
                         handleStatusSelect(selectedTaskId, 'On Hold');
                       }
                       setStatusDropdownAnchor(null);
                       setSelectedTaskId(null);
                     }}
                     sx={{
                       backgroundColor: isDarkMode ? 'grey.700' : '#ffebee',
                       color: '#ef5350',
                       fontWeight: 'medium',
                       '&:hover': {
                         backgroundColor: isDarkMode ? 'grey.600' : '#ffcdd2'
                       }
                     }}
                   >
                     On Hold
                   </MenuItem>,
                   <MenuItem
                     key="close"
                     onClick={() => {
                       if (selectedTaskId) {
                         handleStatusSelect(selectedTaskId, 'Close');
                       }
                       setStatusDropdownAnchor(null);
                       setSelectedTaskId(null);
                     }}
                     sx={{
                       backgroundColor: isDarkMode ? 'grey.700' : '#e8f5e8',
                       color: isDarkMode ? '#81c784' : '#2e7d32',
                       fontWeight: 'medium',
                       '&:hover': {
                         backgroundColor: isDarkMode ? 'grey.600' : '#c8e6c9'
                       }
                     }}
                   >
                     Close
                   </MenuItem>,
                 ];
               }
              
                             if (currentStatus === 'In Progress') {
                 return [
                   <MenuItem
                     key="on-hold"
                     onClick={() => {
                       if (selectedTaskId) {
                         handleStatusSelect(selectedTaskId, 'On Hold');
                       }
                       setStatusDropdownAnchor(null);
                       setSelectedTaskId(null);
                     }}
                     sx={{
                       backgroundColor: isDarkMode ? 'grey.700' : '#ffebee',
                       color: '#ef5350',
                       fontWeight: 'medium',
                       '&:hover': {
                         backgroundColor: isDarkMode ? 'grey.600' : '#ffcdd2'
                       }
                     }}
                   >
                     On Hold
                   </MenuItem>,
                   <MenuItem
                     key="close"
                     onClick={() => {
                       if (selectedTaskId) {
                         handleStatusSelect(selectedTaskId, 'Close');
                       }
                       setStatusDropdownAnchor(null);
                       setSelectedTaskId(null);
                     }}
                     sx={{
                       backgroundColor: isDarkMode ? 'grey.700' : '#e8f5e8',
                       color: isDarkMode ? '#81c784' : '#2e7d32',
                       fontWeight: 'medium',
                       '&:hover': {
                         backgroundColor: isDarkMode ? 'grey.600' : '#c8e6c9'
                       }
                     }}
                   >
                     Close
                   </MenuItem>,
                 ];
               }
              
              if (currentStatus === 'On Hold') {
                return [
                  <MenuItem
                    key="in-progress"
                    onClick={() => {
                      if (selectedTaskId) {
                        handleStatusSelect(selectedTaskId, 'In Progress');
                      }
                      setStatusDropdownAnchor(null);
                      setSelectedTaskId(null);
                    }}
                    sx={{
                      backgroundColor: isDarkMode ? 'grey.700' : '#e3f2fd',
                      color: isDarkMode ? '#90caf9' : '#1976d2',
                      fontWeight: 'medium',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'grey.600' : '#bbdefb'
                      }
                    }}
                  >
                    In Progress
                  </MenuItem>,
                  <MenuItem
                    key="close"
                    onClick={() => {
                      if (selectedTaskId) {
                        handleStatusSelect(selectedTaskId, 'Close');
                      }
                      setStatusDropdownAnchor(null);
                      setSelectedTaskId(null);
                    }}
                    sx={{
                      backgroundColor: isDarkMode ? 'grey.700' : '#e8f5e8',
                      color: isDarkMode ? '#81c784' : '#2e7d32',
                      fontWeight: 'medium',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'grey.600' : '#c8e6c9'
                      }
                    }}
                  >
                    Close
                  </MenuItem>,
                ];
              }
              
              return null;
            })()}
          </Menu>

          {/* Time Filter Menu */}
          <Menu
            anchorEl={timeFilterMenuAnchor}
            open={Boolean(timeFilterMenuAnchor)}
            onClose={() => {
              setTimeFilterMenuAnchor(null);
              setShowCustomRange(false);
              setCustomStartDate('');
              setCustomEndDate('');
            }}
            PaperProps={{
              sx: {
                minWidth: '200px',
                p: 1
              }
            }}
          >
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Today');
              setTimeFilterMenuAnchor(null);
            }}>
              Today
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Yesterday');
              setTimeFilterMenuAnchor(null);
            }}>
              Yesterday
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Last 24 hrs');
              setTimeFilterMenuAnchor(null);
            }}>
              Last 24 hrs
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Last 48 hrs');
              setTimeFilterMenuAnchor(null);
            }}>
              Last 48 hrs
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Last 7 Days');
              setTimeFilterMenuAnchor(null);
            }}>
              Last 7 Days
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Last 30 Days');
              setTimeFilterMenuAnchor(null);
            }}>
              Last 30 Days
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'This Month');
              setTimeFilterMenuAnchor(null);
            }}>
              This Month
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'Last Month');
              setTimeFilterMenuAnchor(null);
            }}>
              Last Month
            </MenuItem>
            <MenuItem onClick={() => {
              handleFilterChange('timeFilter', 'All');
              setTimeFilterMenuAnchor(null);
            }}>
              All
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setShowCustomRange(!showCustomRange);
              }}
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}
            >
              Custom Range
              <ArrowDownIcon fontSize="small" />
            </MenuItem>
            {showCustomRange && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ mb: 2 }}>
                  <TextField

                    type="date"
                    label="Start Date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    type="date"
                    label="End Date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (customStartDate && customEndDate) {
                        handleFilterChange('timeFilter', `Custom: ${customStartDate} to ${customEndDate}`);
                        setTimeFilterMenuAnchor(null);
                        setShowCustomRange(false);
                        setCustomStartDate('');
                        setCustomEndDate('');
                      }
                    }}
                    disabled={!customStartDate || !customEndDate}
                    sx={{ flex: 1 }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setShowCustomRange(false);
                      setCustomStartDate('');
                      setCustomEndDate('');
                    }}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Menu>
     </Box>
   );
 };

export default TasksPage;
